import React, { useEffect } from "react";
import {useLIDContext} from "../../contexts/LIDContext";
import DataSnackbar from "../SideBar/DataSnackbar";

// For Map component
import MapDisplay from "./MapDisplay";
import { useMapContext } from "../../contexts/MapContext";

// For Map Control Panel component
import {
    getColorLayer,
    getLIDLayer,
    getOutfallColor,
    getSelectedFeatureBorder,
    getBrushedSubcatchLayer,
} from "./MapControlPanel/getColorFunctions";
import MapControlPanel from "./MapControlPanel/MapControlPanel";

// For map legend
import Legend from "./Legend";

import LIDPanel from "../LID/LIDPanel";
import SystemTotalsPanel from '../SystemTotals/SystemTotalsPanel';
import SideBar from "../SideBar/SideBar";
import FloodMapLoadingMsg from "../Flooding/FloodMapLoadingMsg";
import LoadingLogoSmall from "../../utils/LoadingLogoSmall";
import ViewBaselineButton from "../Flooding/ViewBaselineButton/ViewBaselineButton";
import BrushEraserToolbar from "../BrushEraser/BrushEraserToolBar";
import LIDAgent from "../LID/Agent/LIDAgent";


const FLOODING_ENABLED = process.env.REACT_APP_ENABLE_FLOODING_FEATURES === 'TRUE';

function MapComponent() {
    const {
        userData,
        selectedFeature,
        colorScheme,
        LIDMode,
        LIDContent,
        setGeoJSONData,
        selectedStormOption,
        userDevPlansFromS3,
        setUserDevPlansFromS3,
        containPlan,
        devPlanID,
        showFloodMapMsg,
        setShowFloodMapMsg,
        loadingFloodMap,
        plansStillRunning, 
        setPlansStillRunning,
        setEditMode,
        setBrushMode,
        setEraserMode,
        setHandMode,
        pendingOperations,
    } = useMapContext();
    const {
        devPlanContent,
        brushedSubcatchments,
        setBrushedSubcatchments,
    } = useLIDContext();

    const floodingMapExist = userData && containPlan(selectedStormOption, userData.id, devPlanID);
    // const [reloadMapControlPanel, setReloadMapControlPanel] = useState(false);
    let subcatchmentsjson = '/map_layers/Subcatchments.geojson';
    let outfallsjson = '/map_layers/Outfalls.geojson';
    const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY;
    if (OPENROUTER_API_KEY === "none") {
        // This means we're on the main branch.
        subcatchmentsjson = '/map_layers/updated_subcatchments.geojson';
        outfallsjson = '/map_layers/CleanOutfalls.geojson';
    }

    // Load the GeoJSON data from the server 
    useEffect(() => {
        fetch(subcatchmentsjson)
            .then(response => response.json())
            .then(data => setGeoJSONData(prevData => ({...prevData, subcatchments: data})))
            .catch(error => console.error('Error loading GeoJSON:', error));
        fetch(outfallsjson)
            .then(response => response.json())
            .then(data => setGeoJSONData(prevData => ({...prevData, outfalls: data})))
            .catch(error => console.error('Error loading GeoJSON:', error));
        fetch('/map_layers/Mar27_Overflows.json')
            .then(response => response.json())
            .then(data => setGeoJSONData(prevData => ({...prevData, overflowData: data})))
            .catch(error => console.error('Error loading Overflow JSON:', error));
    }, []);

    useEffect(() => {
        fetch(`/map_layers/${selectedStormOption}_Overflows.json`)
            .then(response => response.json())
            .then(data => setGeoJSONData(prevData => ({...prevData, overflowData: data})))
            .catch(error => console.error('Error loading Overflow JSON:', error));
    }, [selectedStormOption]);


    // Preload data for a default subcatchment
    useEffect(() => {
        const preloadData = async () => {
            try {
                // Choose a default subcatchment ID
                //const defaultSubcatchmentId = 'WRRF_001'; // Replace with an actual subcatchment ID
                // Need this so that they all run concurrently and not one after another.
                // These don't have to fetch this data, it just has to be any api endpoint.
                //await Promise.all([
                //]);
                console.log('Preloaded data for default subcatchment');
            } catch (error) {
                console.error('Error preloading data:', error);
            }
        };

        preloadData();
    }, []);


    // This function check which plan is still running by matching the their inp and out file on S3.
    const checkWhichPlansAreStillRunning = (data) => {
        const { devPlan_filenames: uniqueNames, inp_files: allInps, out_files: allOuts } = data;

        return uniqueNames.filter(filename => 
            !allInps.includes(`${filename}.inp`) || !allOuts.includes(`${filename}.out`)
        );
    };


    // Get all dev plans associated with the user from AWS S3 bucket "kensho-result"
    useEffect(() => {
        if (! userData || !userData.id) return;
        //console.log("CHECK : useEffect to get filenames from S3. plan : ", devPlanID);

        let intervalId;

        const getAllDevPlanFiles = async (user_id) => {
            try {
                const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/getAllDevPlansFromUser/${user_id}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Failed to fetch all dev plan filenames from S3 bucket kensho-results from user : ${user_id}`);
                const data = await response.json();

                const haveSameElements = (arr1, arr2) => {
                    if (arr1.length !== arr2.length) return false;

                    const set1 = new Set(arr1);
                    return arr2.every(item => set1.has(item));
                }

                if (haveSameElements(userDevPlansFromS3, data['devPlan_filenames'])) {
                    return; // No change in items, no need to update
                }
                setUserDevPlansFromS3(data['devPlan_filenames']);


                const onGoingPlans = checkWhichPlansAreStillRunning(data);
                if (haveSameElements(plansStillRunning, onGoingPlans)) {
                    return; //  No change in items, no need to update
                }
                setPlansStillRunning(onGoingPlans);
            } catch (error) {
                console.error("Error fetching dev plan filenames:", error);
            }
        };

        // initial fetch
        getAllDevPlanFiles(userData.id);
        console.log("MAP : Initially fetched filenames from S3.");

        if (plansStillRunning.length > 0) {
            // fetch every 60 seconds if the model is still running for a storm and plan
            intervalId = setInterval(() => {
                getAllDevPlanFiles(userData.id);
                console.log("MAP : Polling S3 filenames every 60 sec.");
            }, 60000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
                console.log("MAP : Stopped polling S3 filenames");
            }
        };

    }, [userData]);


    // Preload all dev plans and upload all geojson files for flooding and basement flooding
    useEffect(() => {
        const preloadData  = async () => {
            if (!userData?.developmentPlans?.items) return null;
            try {
                const preprocessing_filenames = (devPlanFilenames, plansStillRunning, devPlansFromUserData) => {
                    //console.log(" PROCESS FILENAMES plansStillRunning : ", plansStillRunning);
                    return devPlanFilenames
                        .map(filename => {
                            const components = filename.split("_");
                            if (components.length < 4) return null;

                            const storm = components[1];
                            const user_id = components[2];
                            const plan = components[3];

                            const formattedFilename = `GLWA_${storm}_${user_id}_${plan}`;

                            // make sure that each plan name is in the current list of plans.
                            const isPlanFromUser = devPlansFromUserData.includes(plan);


                            return (!plansStillRunning.includes(formattedFilename) && isPlanFromUser)
                                ? `${storm}/${user_id}_${plan}`
                                : null;
                        })
                        .filter(Boolean); // Removes `null` values
                };

                // const devPlanFilenames = await getAllDevPlanFiles(userData.id);

                const devPlanFilenames = userDevPlansFromS3;

                const devPlansFromUserData = userData?.developmentPlans?.items.map(plan => plan.id);

                const storm_dev_plan = preprocessing_filenames(devPlanFilenames, plansStillRunning, devPlansFromUserData);

                console.log("* storm_dev_plan : ", storm_dev_plan);

                //preUploadAllGeoJsonFiles_flooding(storm_dev_plan, plansStillRunning);
                //preUploadAllGeoJsonFiles_basementFlooding(storm_dev_plan);

            } catch (error) {
                console.error("Error in preloadData:", error);
            }
        }

        preloadData();
    }, [userDevPlansFromS3, plansStillRunning]);


    // Show a message on the flooding and basement flooding if users created a new plan but not run the model yet
    useEffect(() => {
        if (!FLOODING_ENABLED) return;
        if ((colorScheme === "flooding") || (colorScheme === "basementFlooding")) {
            const floodingMapExist = userData && containPlan(selectedStormOption, userData.id, devPlanID);
            setShowFloodMapMsg(!floodingMapExist);
        } else {
            setShowFloodMapMsg(false);
        }
    }, [colorScheme, selectedStormOption, userData, devPlanID])

    useEffect(() => {
        if (!FLOODING_ENABLED) return;
        if (floodingMapExist) {
            setBrushedSubcatchments(new Set());
            setBrushMode(false);
            setEraserMode(false);
            setHandMode(true);
            setEditMode(false);
        }
    }, [floodingMapExist, setBrushedSubcatchments])


    useEffect(() => {
        console.log("Dev Plan ID : ", devPlanID);
        console.log("UserData ID : ", userData);
    }, [devPlanID, userData])

    return (
        <div className="map-container">
            <Legend/>
            <SideBar/>
            <LIDPanel/>
            <SystemTotalsPanel/>


            { FLOODING_ENABLED && showFloodMapMsg && <FloodMapLoadingMsg setShowMsg={setShowFloodMapMsg} message="Showing baseline flooding. Run the model to see your plan's results." /> }
            { FLOODING_ENABLED && loadingFloodMap && <LoadingLogoSmall message={"Loading Flooding Data ..."} /> }

            <MapDisplay>
                {/* {!floodingMapExist && <BrushedSubcatchmentList />} */}
                {/*!floodingMapExist &&<BrushEraserToolbar />*/}
                {pendingOperations === 0 &&
                    <>
                        <BrushEraserToolbar/>
                        <LIDAgent/>
                    </>
                }
                <MapControlPanel
                    getColorLayer={() => getColorLayer(colorScheme, selectedFeature.ID, LIDMode, LIDContent, devPlanContent, brushedSubcatchments)}
                    getLIDLayer={() => getLIDLayer(colorScheme, selectedFeature.ID, LIDMode, LIDContent, devPlanContent)}
                    getSelectedFeatureBorder={() => getSelectedFeatureBorder(colorScheme, selectedFeature.ID, LIDMode, LIDContent, devPlanContent)}
                    getBrushedSubcatchLayer={() => getBrushedSubcatchLayer(brushedSubcatchments)}
                    getOutfallColor={getOutfallColor}
                />
            </MapDisplay>

            { FLOODING_ENABLED && (colorScheme==="flooding" || colorScheme==="basementFlooding") && < ViewBaselineButton/>}

        </div>
    );
}

export default MapComponent;
